// §6.1 — Multilingual welcome screen for /submit.
// Detected from navigator.language; falls back to English.
// Maintained separately from the main i18n catalog so it can be extended
// without a full translation pass. Owner reviews any new language before
// merge. Each entry is ~100–150 words covering the six points in §6.1.

export type WelcomeMessage = {
  /** Native language name shown in the language hint. */
  nativeName: string;
  /** BCP-47 language tag for the <html lang> attribute on the welcome screen. */
  htmlLang: string;
  /** "rtl" for Arabic. */
  dir?: "ltr" | "rtl";
  /** Short headline (one line). */
  heading: string;
  /** The six points, in order. */
  points: [string, string, string, string, string, string];
  /** Button label, e.g. "Enter". */
  cta: string;
  /** Footer line repeating /help link. */
  helpLine: string;
};

const en: WelcomeMessage = {
  nativeName: "English",
  htmlLang: "en",
  heading: "Before you tell your story",
  points: [
    "You can write in your own language. We accept stories in English, French, Spanish, Russian, Chinese, Arabic, Tagalog, Hindi, Indonesian, Ukrainian, Polish, Portuguese and Greek — and we will translate yours.",
    "Words used to insult, humiliate or threaten you will be kept exactly as you wrote them — in the original, and in accurate translation. We do not censor testimony.",
    "You are completely anonymous. No name, no ship's name, no IP address, no tracking on this page.",
    "If you are in danger right now, open the Help page first.",
    "An AI companion is available to help you tell your story. It is not a lawyer, not a crisis service, not a human. It will not store your words until you press Send.",
    "This site is for adults (18+). Stories may contain strong language.",
  ],
  cta: "I understand — continue",
  helpLine: "In danger right now? → Open Help",
};

const ru: WelcomeMessage = {
  nativeName: "Русский",
  htmlLang: "ru",
  heading: "Прежде чем рассказать свою историю",
  points: [
    "Вы можете писать на своём языке. Мы принимаем истории на английском, французском, испанском, русском, китайском, арабском, тагальском, хинди, индонезийском, украинском, польском, португальском и греческом — и переведём вашу.",
    "Слова, которыми вас оскорбляли, унижали или угрожали вам, мы сохраним так, как вы их написали — в оригинале и в точном переводе. Мы не цензурируем свидетельства.",
    "Вы полностью анонимны. Никакого имени, названия судна, IP-адреса. На этой странице нет никакой слежки.",
    "Если опасность прямо сейчас — сначала откройте страницу «Помощь».",
    "Рядом доступен ИИ-собеседник, который поможет вам выстроить рассказ. Это не юрист, не служба кризисной помощи и не человек. Ваши слова не сохраняются, пока вы не нажмёте «Отправить».",
    "Сайт для взрослых (18+). В историях может встречаться крепкая речь.",
  ],
  cta: "Понимаю — продолжить",
  helpLine: "Опасность сейчас? → Открыть «Помощь»",
};

const fr: WelcomeMessage = {
  nativeName: "Français",
  htmlLang: "fr",
  heading: "Avant de raconter votre histoire",
  points: [
    "Vous pouvez écrire dans votre propre langue. Nous acceptons les récits en anglais, français, espagnol, russe, chinois, arabe, tagalog, hindi, indonésien, ukrainien, polonais, portugais et grec — et nous traduirons le vôtre.",
    "Les mots utilisés pour vous insulter, vous humilier ou vous menacer seront conservés tels que vous les avez écrits — dans la langue d'origine et en traduction fidèle. Nous ne censurons pas les témoignages.",
    "Vous êtes complètement anonyme. Aucun nom, aucun nom de navire, aucune adresse IP, aucun traçage sur cette page.",
    "Si vous êtes en danger maintenant, ouvrez d'abord la page Aide.",
    "Un compagnon IA peut vous aider à raconter votre histoire. Ce n'est ni un avocat, ni un service de crise, ni un humain. Vos mots ne sont pas conservés tant que vous n'avez pas appuyé sur Envoyer.",
    "Ce site est réservé aux adultes (18+). Les récits peuvent contenir un langage cru.",
  ],
  cta: "Je comprends — continuer",
  helpLine: "En danger maintenant ? → Ouvrir Aide",
};

const es: WelcomeMessage = {
  nativeName: "Español",
  htmlLang: "es",
  heading: "Antes de contar tu historia",
  points: [
    "Puedes escribir en tu propio idioma. Aceptamos relatos en inglés, francés, español, ruso, chino, árabe, tagalo, hindi, indonesio, ucraniano, polaco, portugués y griego — y traduciremos el tuyo.",
    "Las palabras usadas para insultarte, humillarte o amenazarte se conservan tal como las escribiste — en el original y en traducción fiel. No censuramos testimonios.",
    "Eres completamente anónimo. Ningún nombre, ningún nombre del buque, ninguna dirección IP, ningún rastreo en esta página.",
    "Si estás en peligro ahora, abre primero la página de Ayuda.",
    "Un asistente de IA puede ayudarte a estructurar tu historia. No es abogado, ni servicio de crisis, ni humano. Tus palabras no se guardan hasta que pulses Enviar.",
    "Este sitio es para adultos (18+). Los relatos pueden contener lenguaje fuerte.",
  ],
  cta: "Entiendo — continuar",
  helpLine: "¿En peligro ahora? → Abrir Ayuda",
};

const zh: WelcomeMessage = {
  nativeName: "中文",
  htmlLang: "zh-Hans",
  heading: "在讲述您的故事之前",
  points: [
    "您可以用自己的母语写作。我们接受英语、法语、西班牙语、俄语、中文、阿拉伯语、他加禄语、印地语、印尼语、乌克兰语、波兰语、葡萄牙语和希腊语的稿件——我们会负责翻译。",
    "侮辱、羞辱或威胁您的原话,将按您所写的样子完整保留——原文和忠实译文一并刊出。我们不审查证词。",
    "您完全匿名。不记录姓名、船名、IP 地址,此页无任何跟踪。",
    "如果您此刻正处于危险之中,请先打开「帮助」页面。",
    "侧边的 AI 助手可以帮助您组织讲述。它不是律师、不是危机服务、不是人类。在您按下「发送」之前,您的话不会被保存。",
    "本网站面向成年人(18+)。故事中可能出现粗俗语言。",
  ],
  cta: "我已了解 — 继续",
  helpLine: "现在处于危险? → 打开「帮助」",
};

const ar: WelcomeMessage = {
  nativeName: "العربية",
  htmlLang: "ar",
  dir: "rtl",
  heading: "قبل أن تروي قصتك",
  points: [
    "يمكنك الكتابة بلغتك. نقبل القصص بالإنجليزية والفرنسية والإسبانية والروسية والصينية والعربية والتاغالوغ والهندية والإندونيسية والأوكرانية والبولندية والبرتغالية واليونانية — وسنترجم قصتك.",
    "الكلمات التي استُخدمت لإهانتك أو إذلالك أو تهديدك تُحفظ كما كتبتها تمامًا — في لغتها الأصلية ومع ترجمة دقيقة. لا نُمارس الرقابة على الشهادات.",
    "أنت مجهول الهوية تمامًا. لا اسم، ولا اسم سفينة، ولا عنوان IP، ولا أي تتبع في هذه الصفحة.",
    "إذا كنت في خطر الآن، افتح صفحة «المساعدة» أولًا.",
    "يتوفر مساعد ذكاء اصطناعي لمساعدتك على ترتيب روايتك. ليس محاميًا، وليس خدمة طوارئ، وليس إنسانًا. لن تُحفظ كلماتك إلى أن تضغط «إرسال».",
    "هذا الموقع للبالغين (18+). قد تحتوي القصص على ألفاظ شديدة.",
  ],
  cta: "فهمت — متابعة",
  helpLine: "في خطر الآن؟ ← افتح المساعدة",
};

const tl: WelcomeMessage = {
  nativeName: "Tagalog",
  htmlLang: "tl",
  heading: "Bago mo ikuwento ang iyong istorya",
  points: [
    "Maaari kang sumulat sa iyong sariling wika. Tumatanggap kami ng kuwento sa Ingles, Pranses, Espanyol, Ruso, Tsino, Arabe, Tagalog, Hindi, Indones, Ukraniano, Polako, Portuges at Griyego — at isasalin namin ang iyo.",
    "Ang mga salitang ginamit upang insultuhin, ipahiya, o pagbantaan ka ay itatago nang eksakto sa paraang isinulat mo — sa orihinal at sa tumpak na pagsasalin. Hindi kami nagse-censor ng testimonya.",
    "Ganap kang anonymous. Walang pangalan, walang pangalan ng barko, walang IP address, walang tracking sa pahinang ito.",
    "Kung nasa panganib ka ngayon, buksan muna ang pahina ng Tulong.",
    "May AI na katuwang na pwede mong gamitin para tulungan kang ayusin ang iyong kuwento. Hindi ito abogado, hindi crisis service, hindi tao. Hindi maiimbak ang iyong mga salita hangga't hindi mo pinipindot ang Ipadala.",
    "Ang site na ito ay para sa nasa hustong gulang (18+). Maaaring may matatapang na salita.",
  ],
  cta: "Naiintindihan ko — magpatuloy",
  helpLine: "Nasa panganib ngayon? → Buksan ang Tulong",
};

const hi: WelcomeMessage = {
  nativeName: "हिन्दी",
  htmlLang: "hi",
  heading: "अपनी कहानी बताने से पहले",
  points: [
    "आप अपनी भाषा में लिख सकते हैं। हम अंग्रेज़ी, फ़्रेंच, स्पेनिश, रूसी, चीनी, अरबी, टैगालॉग, हिन्दी, इंडोनेशियाई, यूक्रेनी, पोलिश, पुर्तगाली और ग्रीक में कहानियाँ स्वीकार करते हैं — और आपकी कहानी का अनुवाद करेंगे।",
    "जिन शब्दों से आपको अपमानित, ज़लील या धमकाया गया, वे ठीक उसी रूप में रखे जाएँगे जैसा आपने लिखा है — मूल में और सटीक अनुवाद में। हम गवाही सेंसर नहीं करते।",
    "आप पूरी तरह से अनाम हैं। कोई नाम नहीं, कोई जहाज़ का नाम नहीं, कोई IP पता नहीं, इस पेज पर कोई ट्रैकिंग नहीं।",
    "यदि आप अभी ख़तरे में हैं, तो पहले Help पेज खोलें।",
    "एक AI साथी उपलब्ध है जो आपकी कहानी कहने में मदद कर सकता है। यह वकील नहीं है, संकट सेवा नहीं है, मनुष्य नहीं है। जब तक आप Send नहीं दबाते, आपके शब्द कहीं सहेजे नहीं जाते।",
    "यह साइट वयस्कों (18+) के लिए है। कहानियों में कठोर भाषा हो सकती है।",
  ],
  cta: "मैं समझ गया — आगे बढ़ें",
  helpLine: "अभी ख़तरे में? → Help खोलें",
};

const id: WelcomeMessage = {
  nativeName: "Bahasa Indonesia",
  htmlLang: "id",
  heading: "Sebelum Anda menceritakan kisah Anda",
  points: [
    "Anda boleh menulis dalam bahasa Anda sendiri. Kami menerima kisah dalam bahasa Inggris, Prancis, Spanyol, Rusia, Mandarin, Arab, Tagalog, Hindi, Indonesia, Ukraina, Polandia, Portugis, dan Yunani — dan kami akan menerjemahkannya.",
    "Kata-kata yang dipakai untuk menghina, mempermalukan, atau mengancam Anda akan kami simpan persis seperti yang Anda tulis — dalam bahasa aslinya dan dalam terjemahan yang akurat. Kami tidak menyensor kesaksian.",
    "Anda sepenuhnya anonim. Tidak ada nama, tidak ada nama kapal, tidak ada alamat IP, tidak ada pelacakan di halaman ini.",
    "Jika Anda dalam bahaya sekarang, buka halaman Bantuan terlebih dahulu.",
    "Tersedia pendamping AI yang dapat membantu menyusun cerita Anda. Ia bukan pengacara, bukan layanan krisis, bukan manusia. Kata-kata Anda tidak disimpan sampai Anda menekan Kirim.",
    "Situs ini untuk dewasa (18+). Cerita dapat memuat bahasa kasar.",
  ],
  cta: "Saya mengerti — lanjutkan",
  helpLine: "Dalam bahaya sekarang? → Buka Bantuan",
};

const uk: WelcomeMessage = {
  nativeName: "Українська",
  htmlLang: "uk",
  heading: "Перш ніж розповісти свою історію",
  points: [
    "Ви можете писати своєю мовою. Ми приймаємо історії англійською, французькою, іспанською, російською, китайською, арабською, тагальською, гінді, індонезійською, українською, польською, португальською та грецькою — і перекладемо вашу.",
    "Слова, якими вас ображали, принижували чи погрожували, ми збережемо саме так, як ви їх написали — в оригіналі та в точному перекладі. Ми не цензуруємо свідчення.",
    "Ви повністю анонімні. Жодного імені, жодної назви судна, жодної IP-адреси, жодного відстеження на цій сторінці.",
    "Якщо ви в небезпеці прямо зараз — спершу відкрийте сторінку «Допомога».",
    "Поряд доступний ШІ-співрозмовник, який допоможе впорядкувати розповідь. Це не юрист, не служба екстреної допомоги і не людина. Ваші слова не зберігаються, доки ви не натиснете «Надіслати».",
    "Сайт для дорослих (18+). В історіях може траплятися міцна лексика.",
  ],
  cta: "Розумію — продовжити",
  helpLine: "У небезпеці зараз? → Відкрити «Допомогу»",
};

const pl: WelcomeMessage = {
  nativeName: "Polski",
  htmlLang: "pl",
  heading: "Zanim opowiesz swoją historię",
  points: [
    "Możesz pisać we własnym języku. Przyjmujemy relacje po angielsku, francusku, hiszpańsku, rosyjsku, chińsku, arabsku, tagalsku, hindi, indonezyjsku, ukraińsku, polsku, portugalsku i grecku — twoją przetłumaczymy.",
    "Słowa, którymi cię obrażano, poniżano lub ci grożono, zachowamy dokładnie tak, jak je zapisałeś — w oryginale i w wiernym tłumaczeniu. Nie cenzurujemy świadectw.",
    "Jesteś w pełni anonimowy. Żadnego imienia, żadnej nazwy statku, żadnego adresu IP, żadnego śledzenia na tej stronie.",
    "Jeśli jesteś teraz w niebezpieczeństwie, otwórz najpierw stronę Pomoc.",
    "Obok dostępny jest towarzysz AI, który pomoże uporządkować opowieść. To nie prawnik, nie służba kryzysowa, nie człowiek. Twoje słowa nie są zapisywane, dopóki nie naciśniesz Wyślij.",
    "Strona dla dorosłych (18+). W historiach może pojawić się mocny język.",
  ],
  cta: "Rozumiem — dalej",
  helpLine: "Zagrożenie teraz? → Otwórz Pomoc",
};

const ptBR: WelcomeMessage = {
  nativeName: "Português (Brasil)",
  htmlLang: "pt-BR",
  heading: "Antes de contar sua história",
  points: [
    "Você pode escrever na sua própria língua. Aceitamos relatos em inglês, francês, espanhol, russo, chinês, árabe, tagalo, hindi, indonésio, ucraniano, polonês, português e grego — e traduziremos o seu.",
    "As palavras usadas para te insultar, humilhar ou ameaçar serão mantidas exatamente como você as escreveu — no original e em tradução fiel. Não censuramos testemunhos.",
    "Você é totalmente anônimo. Sem nome, sem nome do navio, sem endereço IP, sem rastreamento nesta página.",
    "Se você estiver em perigo agora, abra primeiro a página de Ajuda.",
    "Um acompanhante de IA está disponível para ajudar a organizar sua história. Não é advogado, não é serviço de crise, não é humano. Suas palavras não são guardadas até você apertar Enviar.",
    "Este site é para adultos (18+). Os relatos podem conter linguagem forte.",
  ],
  cta: "Entendi — continuar",
  helpLine: "Em perigo agora? → Abrir Ajuda",
};

const el: WelcomeMessage = {
  nativeName: "Ελληνικά",
  htmlLang: "el",
  heading: "Πριν αφηγηθείτε την ιστορία σας",
  points: [
    "Μπορείτε να γράψετε στη δική σας γλώσσα. Δεχόμαστε ιστορίες στα Αγγλικά, Γαλλικά, Ισπανικά, Ρωσικά, Κινεζικά, Αραβικά, Ταγκαλόγκ, Χίντι, Ινδονησιακά, Ουκρανικά, Πολωνικά, Πορτογαλικά και Ελληνικά — και θα μεταφράσουμε τη δική σας.",
    "Οι λέξεις που χρησιμοποιήθηκαν για να σας προσβάλουν, να σας ταπεινώσουν ή να σας απειλήσουν θα κρατηθούν ακριβώς όπως τις γράψατε — στο πρωτότυπο και σε ακριβή μετάφραση. Δεν λογοκρίνουμε μαρτυρίες.",
    "Είστε εντελώς ανώνυμος. Κανένα όνομα, κανένα όνομα πλοίου, καμία διεύθυνση IP, καμία παρακολούθηση σε αυτήν τη σελίδα.",
    "Αν κινδυνεύετε αυτή τη στιγμή, ανοίξτε πρώτα τη σελίδα Βοήθεια.",
    "Διατίθεται συνοδός AI για να σας βοηθήσει να οργανώσετε την ιστορία σας. Δεν είναι δικηγόρος, δεν είναι υπηρεσία κρίσης, δεν είναι άνθρωπος. Τα λόγια σας δεν αποθηκεύονται μέχρι να πατήσετε Αποστολή.",
    "Ο ιστότοπος απευθύνεται σε ενηλίκους (18+). Οι ιστορίες μπορεί να περιέχουν σκληρή γλώσσα.",
  ],
  cta: "Κατάλαβα — συνέχεια",
  helpLine: "Κίνδυνος τώρα; → Άνοιγμα Βοήθειας",
};

/** Code -> message. Keep this map flat; lookup uses normalized BCP-47. */
const CATALOG: Record<string, WelcomeMessage> = {
  en, ru, fr, es, zh, ar, tl, hi, id, uk, pl, el,
  "pt-BR": ptBR,
};

/** Allowed list, for UI hints. Order is rollout priority. */
export const WELCOME_LANGS = [
  "en", "ru", "fr", "es", "zh", "ar",
  "tl", "hi", "id", "uk", "pl", "pt-BR", "el",
] as const;

/**
 * Pick the closest welcome message for a navigator.language tag.
 * Falls back to English when nothing matches.
 */
export function pickWelcome(navLang: string | undefined | null): WelcomeMessage {
  if (!navLang) return en;
  const tag = navLang.trim();
  if (!tag) return en;
  // Exact match first (e.g. "pt-BR").
  if (CATALOG[tag]) return CATALOG[tag];
  // Region-stripped match (e.g. "fr-CA" -> "fr").
  const base = tag.split("-")[0]?.toLowerCase() ?? "";
  // Portuguese: route any pt-* (including pt-PT) to the Brazilian copy
  // until a separate pt-PT translation lands.
  if (base === "pt") return ptBR;
  // Chinese variants -> simplified.
  if (base === "zh") return zh;
  if (CATALOG[base]) return CATALOG[base];
  return en;
}

export function getWelcomeByCode(code: string): WelcomeMessage {
  return CATALOG[code] ?? en;
}